import React, { useMemo } from "react";
import { object, func } from "prop-types";
import { Table, Button } from "reactstrap";
import { isNil, isEmpty } from "ramda";
import { useSelector } from "react-redux";

import { oidcSelector } from "../../../../../../selectors/oidcSelectors";

import MonitoringQueueEntrySearchDetailsTableRow from "./row";
import "./monitoringQueueEntrySearchDetailsTable.css";

import { MONITORING_QUEUE_ENTRY_TYPES } from "../../../../constants/monitoringQueueEntryConstants";
import { createWatchListIgnoringPropertyIsLoading } from "../../../../selectors";
import FullscreenLoader from "../../../../../../layouts/FullScreenLoader";
import ReactTooltip from "react-tooltip";

const MonitoringQueueEntrySearchDetailsTable = ({
  details,
  toggleDetails,
  toggleSave,
  permitConfirm,
}) => {
  let list = {};

  const { type } = details;

  switch (type) {
    case MONITORING_QUEUE_ENTRY_TYPES.WATCHLIST_MANUAL_SEARCH:
      list = details.clientManualSearchingResult.watchListMatchesList;
      break;
    case MONITORING_QUEUE_ENTRY_TYPES.WATCHLIST_REGULAR_SEARCH:
      list = details.clientRegularSearchingResult.watchListMatchesList;
      break;
    case MONITORING_QUEUE_ENTRY_TYPES.BATCH_SCORING:
    case MONITORING_QUEUE_ENTRY_TYPES.API_SCORING:
    case MONITORING_QUEUE_ENTRY_TYPES.REGULAR_SCORING:
      list = details.clientScoringResult.watchListMatchesList;
      break;
    case MONITORING_QUEUE_ENTRY_TYPES.NEGATIVE_MEDIA_INFO:
    case MONITORING_QUEUE_ENTRY_TYPES.NEGATIVE_MEDIA_ELASTIC_BASED:
      list = [details.clientNegativeMediaCheckResult];
      break;
    default:
  }

  const oidc = useSelector(oidcSelector);
  const isLoadingIgnoringProperty = useSelector(
    createWatchListIgnoringPropertyIsLoading,
  );

  const canChangeWatchlistMatchResolution = !!oidc.user.profile.role.find(
    (x) => {
      return (
        x === "coordinator" ||
        x === "master" ||
        x === "manager" ||
        x === "client"
      );
    },
  );

  const canAddNegativeMediaIgnoringProperty = !!oidc.user.profile.role.find(
    (x) => {
      return (
        x === "coordinator" ||
        x === "manager" ||
        x === "master" ||
        x === "client"
      );
    },
  );

  let confirmButtonVisibility = true;
  if (!isNil(list) && !isEmpty(list)) {
    confirmButtonVisibility = canChangeWatchlistMatchResolution;
  }

  let showNegativeMediaAction = false;
  if (
    type === MONITORING_QUEUE_ENTRY_TYPES.NEGATIVE_MEDIA_INFO ||
    type === MONITORING_QUEUE_ENTRY_TYPES.NEGATIVE_MEDIA_ELASTIC_BASED
  ) {
    showNegativeMediaAction = list[0].url;
    if (list[0].dataJson) {
      var data = JSON.parse(list[0].dataJson);
      showNegativeMediaAction = data.IsPresented;
    }
  }

  const rows = list.map((entry) => {
    return (
      <MonitoringQueueEntrySearchDetailsTableRow
        key={
          type === MONITORING_QUEUE_ENTRY_TYPES.NEGATIVE_MEDIA_INFO ||
          type === MONITORING_QUEUE_ENTRY_TYPES.NEGATIVE_MEDIA_ELASTIC_BASED
            ? entry.monitoringQueueEntryId
            : entry.id
        }
        entry={entry}
        type={type}
        toggleDetails={toggleDetails}
        canAddNegativeMediaIgnoringProperty={
          canAddNegativeMediaIgnoringProperty
        }
      />
    );
  });

  const isNegativeMedia = useMemo(() => {
    return (
      type === MONITORING_QUEUE_ENTRY_TYPES.NEGATIVE_MEDIA_INFO ||
      type === MONITORING_QUEUE_ENTRY_TYPES.NEGATIVE_MEDIA_ELASTIC_BASED
    );
  }, [type]);

  const dateLabelWithTooltip = () => {
    return isNegativeMedia ? (
      "Date of check"
    ) : (
      <React.Fragment>
        <ReactTooltip id="date-of-birth" type="info">
          <span>Date Of Birth</span>
        </ReactTooltip>
        <span data-tip data-for={"date-of-birth"}>
          DoB / Est. date
        </span>
      </React.Fragment>
    );
  };

  const parentDateWithTooltip = () => (
    <React.Fragment>
      <ReactTooltip id="parent-date" type="info">
        <span>Parent Date</span>
      </ReactTooltip>
      <span data-tip data-for={"parent-date"}>
        PD Date
      </span>
    </React.Fragment>
  );

  return (
    rows.length > 0 && (
      <React.Fragment>
        <FullscreenLoader loading={isLoadingIgnoringProperty} />
        <Table className="monitoring-queue-entry-search-details-table">
          <thead className="thead-light">
            <tr>
              <th>{isNegativeMedia ? "URL" : "Watchlist name"}</th>
              {!isNegativeMedia && <th>Watchlist type</th>}
              <th>Full name / Company name</th>
              <th>{dateLabelWithTooltip()}</th>
              {!isNegativeMedia ? (
                <th width={100}>
                  Lowest <b>%</b>
                </th>
              ) : null}
              <th>{parentDateWithTooltip()}</th>
              {isNegativeMedia ? (
                showNegativeMediaAction ? (
                  <th>Decision</th>
                ) : (
                  <React.Fragment></React.Fragment>
                )
              ) : type !==
                MONITORING_QUEUE_ENTRY_TYPES.WATCHLIST_MANUAL_SEARCH ? (
                <th>Decision</th>
              ) : (
                <React.Fragment></React.Fragment>
              )}
              {isNegativeMedia ? (
                showNegativeMediaAction ? (
                  <th>Actions</th>
                ) : (
                  <React.Fragment></React.Fragment>
                )
              ) : type ? (
                <th>Actions</th>
              ) : (
                <React.Fragment></React.Fragment>
              )}
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </Table>
        {type !== MONITORING_QUEUE_ENTRY_TYPES.NEGATIVE_MEDIA_INFO &&
          type !== MONITORING_QUEUE_ENTRY_TYPES.WATCHLIST_MANUAL_SEARCH &&
          type !== MONITORING_QUEUE_ENTRY_TYPES.NEGATIVE_MEDIA_ELASTIC_BASED &&
          confirmButtonVisibility && (
            <div className="d-flex flex-row align-items-center justify-content-end">
              <div className="confirm-info red right">
                {permitConfirm
                  ? "Please press “Confirm Review“ to confirm you have finished reviewing?"
                  : null}
              </div>
              <Button
                onClick={toggleSave}
                className="float-right mq-details-table-confirm-button"
                disabled={!permitConfirm || isLoadingIgnoringProperty}
              >
                Confirm Review
              </Button>
            </div>
          )}
      </React.Fragment>
    )
  );
};

MonitoringQueueEntrySearchDetailsTable.propTypes = {
  details: object.isRequired,
  toggleDetails: func.isRequired,
};

export default MonitoringQueueEntrySearchDetailsTable;



// WEBPACK FOOTER //
// ./src/features/monitoring-queue-entry/components/searching/search-details-form/table/MonitoringQueueEntrySearchDetailsTable.jsx